/** Tiny DOM helpers for the editor panels. Text always goes through textContent, never innerHTML. */

type Props<K extends keyof HTMLElementTagNameMap> = Partial<Omit<HTMLElementTagNameMap[K], 'style' | 'dataset'>> & { dataset?: Record<string, string> };

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props: Props<K> = {}, ...children: (Node | string | null | false)[]): HTMLElementTagNameMap[K] {
  const { dataset, ...rest } = props;
  const e = Object.assign(document.createElement(tag), rest);
  if (dataset) for (const [k, v] of Object.entries(dataset)) e.dataset[k] = v;
  for (const c of children) if (c !== null && c !== false) e.append(c);
  return e;
}

export function button(label: string, onClick: () => void, props: Props<'button'> = {}): HTMLButtonElement {
  const b = h('button', { type: 'button', textContent: label, ...props });
  b.addEventListener('click', onClick);
  return b;
}

/** True when keyboard input belongs to a form control rather than to editor shortcuts. */
export function typingInto(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el?.tagName) return false;
  // Ticking a checkbox or pressing a button leaves focus there; shortcuts must keep working.
  if (el.tagName === 'INPUT' && ['checkbox', 'radio', 'button', 'submit', 'reset', 'color'].includes((el as HTMLInputElement).type)) return false;
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
}

export function hex(colour: number): string {
  return `#${colour.toString(16).padStart(6, '0')}`;
}

export function fromHex(text: string): number {
  return Number.parseInt(text.replace('#', ''), 16) || 0;
}

/** A number formatted for an input field without float noise. */
export function fmt(n: number): string {
  return String(Math.round(n * 1e4) / 1e4);
}

/** A number typed into a field, clamped to [min, max]; `def` when it is not a number. */
export function clampNum(text: string, min: number, max: number, def: number): number {
  const n = Number(text);
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : def;
}
